import React, { useState, useEffect } from 'react';
import SendProjectAttendeeMessageForm from 'components/Forms/SendProjectAttendeeMesageForm';
import FormDialog from './FormDialog';
import { map } from 'lodash';
import { useDeepCompareEffect } from 'react-use';
import Spinner from 'components/Spinner';
import { Box, Button, Typography } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { enqueueErrorSnackbar } from 'redux/actions/notifierActions';
import { PROJECT_ATTENDEE_MESSAGE_DESTINATIONS } from 'constants/staffing';
import * as Sentry from '@sentry/react';
import { useInternalApiClient } from 'hooks/useInternalApiClient';

const SendAttendeeMessageDialog = ({ handleClose, isOpen, projectAttendeeMessages, projectId }) => {
  const dispatch = useDispatch();
  const apiClient = useInternalApiClient();
  const [isFetchingAttendees, setIsFetchingAttendees] = useState(false);
  /** True after the first attendee load attempt finishes — avoids refetch loops when the list is empty. */
  const [hasLoadedAttendees, setHasLoadedAttendees] = useState(false);
  const [attendeeOptions, setAttendeeOptions] = useState([]);
  const [messageOptions, setMessageOptions] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setAttendeeOptions([]);
        setMessageOptions([]);
        setHasLoadedAttendees(false);
      }, 1000);
    }
  }, [isOpen]);

  useDeepCompareEffect(() => {
    (async () => {
      if (isFetchingAttendees || hasLoadedAttendees || !isOpen) return;

      setIsFetchingAttendees(true);

      try {
        const { attendees } = await apiClient.projects.getAttendees(projectId);
        const options = map(attendees ?? {}, (attendee, id) => ({
          value: id,
          name: attendee.email,
        }));

        if (options.length) {
          options.unshift({
            value: 'all',
            name: 'Send to All',
          });
        }

        setAttendeeOptions(options);
      } catch (error) {
        handleClose();
        dispatch(enqueueErrorSnackbar('Error fetching project attendees'));
        Sentry.captureException(error);
        console.error(error);
      } finally {
        setHasLoadedAttendees(true);
        setIsFetchingAttendees(false);
      }
    })();
  }, [
    isFetchingAttendees,
    hasLoadedAttendees,
    isOpen,
    projectId,
    apiClient,
    dispatch,
    handleClose,
  ]);

  useDeepCompareEffect(() => {
    if (!projectAttendeeMessages.length || !isOpen) return;

    const attendeeMessageDestinations = [
      PROJECT_ATTENDEE_MESSAGE_DESTINATIONS.Email,
      PROJECT_ATTENDEE_MESSAGE_DESTINATIONS.CircleDM,
    ];

    setMessageOptions(
      projectAttendeeMessages
        .filter(msg => attendeeMessageDestinations.includes(msg.destination))
        .map(msg => ({
          value: msg.id,
          name: msg.name,
        })),
    );
  }, [projectAttendeeMessages, isOpen]);

  return (
    <FormDialog
      isOpen={isOpen}
      handleClose={handleClose}
      title="Re-Send Attendee Message"
      isTitleHasCloseIcon
    >
      <Box
        style={{ minHeight: 200 }}
        display="flex"
        flexDirection="column"
        className="flex-1 justify-content-center align-items-center"
      >
        {isFetchingAttendees ? (
          <Box display="flex" flexDirection="column" className="flex-1 mt-8">
            <Spinner />
          </Box>
        ) : hasLoadedAttendees && !attendeeOptions.length ? (
          <Box px={2} py={4} textAlign="center" width="100%">
            <Typography variant="h6" gutterBottom>
              No attendees synced
            </Typography>
            <Typography color="textSecondary" className="mb-4">
              This project does not have any attendees on record yet. Run Sync Attendee List from
              the project menu before re-sending messages.
            </Typography>
            <Button variant="outlined" color="primary" onClick={handleClose}>
              CLOSE
            </Button>
          </Box>
        ) : (
          <SendProjectAttendeeMessageForm
            attendeeOptions={attendeeOptions}
            closeDialog={handleClose}
            messageOptions={messageOptions}
            projectId={projectId}
          />
        )}
      </Box>
    </FormDialog>
  );
};

export default SendAttendeeMessageDialog;
